import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ImgHTMLAttributes,
  type ReactNode,
} from 'react'
import { useReducedMotion } from '../hooks/useReducedMotion'

type NativeImgProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'alt' | 'onLoad' | 'onError' | 'placeholder'
>

export interface LazyImageProps extends NativeImgProps {
  src: string
  alt: string
  className?: string
  /** Wrapper className — defaults to `className` for parity with <img>. */
  wrapperClassName?: string
  /** Content to render while the image is offscreen or still loading. */
  placeholder?: ReactNode
  /** CSS color for the solid placeholder background (used when no `placeholder` node is passed). */
  placeholderColor?: string
  /** Content to render when the image fails to load. */
  fallback?: ReactNode
  onLoad?: () => void
  onError?: () => void
  /** IntersectionObserver threshold. Default 0.01. */
  threshold?: number
  /** IntersectionObserver rootMargin. Default '100px'. */
  rootMargin?: string
}

/**
 * Lazy-loaded `<img>` with blur-up placeholder, fade-in on load, error
 * fallback, and IntersectionObserver-driven lazy loading. Falls back to
 * the native `loading="lazy"` attribute when IntersectionObserver is
 * unavailable. Relies on the browser's HTTP cache for re-use.
 *
 * Drop-in replacement for the desktop renderer's `LazyImage`.
 */
export function LazyImage({
  src,
  alt,
  className = '',
  wrapperClassName,
  placeholder,
  placeholderColor,
  fallback,
  onLoad,
  onError,
  threshold = 0.01,
  rootMargin = '100px',
  ...imgProps
}: LazyImageProps) {
  const [inView, setInView] = useState(() => {
    if (typeof window === 'undefined') return false
    // If IntersectionObserver isn't available, show the image immediately
    // and rely on native `loading="lazy"`.
    return typeof window.IntersectionObserver === 'undefined'
  })
  const [loaded, setLoaded] = useState(false)
  const [errored, setErrored] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (typeof window.IntersectionObserver === 'undefined') return
    if (inView) return

    const node = wrapperRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            observer.disconnect()
            break
          }
        }
      },
      { threshold, rootMargin }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [inView, threshold, rootMargin])

  // Reset load/error state if the src changes.
  useEffect(() => {
    setLoaded(false)
    setErrored(false)
  }, [src])

  const handleLoad = useCallback(() => {
    setLoaded(true)
    onLoad?.()
  }, [onLoad])

  const handleError = useCallback(() => {
    setErrored(true)
    onError?.()
  }, [onError])

  const wrapperClass = `relative overflow-hidden ${
    wrapperClassName ?? className
  }`

  const placeholderNode = placeholder ?? (
    <div
      className="w-full h-full bg-bg-secondary animate-pulse"
      style={placeholderColor ? { backgroundColor: placeholderColor } : undefined}
    />
  )

  if (errored) {
    return (
      <div ref={wrapperRef} className={wrapperClass} aria-label={alt} role="img">
        {fallback ?? (
          <div className="w-full h-full bg-bg-secondary flex items-center justify-center text-text-tertiary">
            <div className="text-center p-3">
              <div className="text-2xl mb-1 opacity-60" aria-hidden="true">
                ▢
              </div>
              <div className="text-xs">Image unavailable</div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      {!loaded && (
        <div className="absolute inset-0">{placeholderNode}</div>
      )}
      {inView && (
        <img
          {...imgProps}
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-cover ${
            reduceMotion ? '' : 'transition-opacity duration-300'
          } ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        />
      )}
    </div>
  )
}

export default LazyImage
